package mapsearch.generalMarkers

import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.SiteRestriction
import apiclient.geoobjects.isFlagged
import apiclient.tags.floorId
import com.jillesvangurp.geojson.Geometry
import data.objects.ActiveObjectStore
import data.users.settings.LocalSettingsStore
import dev.fritz2.core.RootStore
import koin.withKoin
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.combine
import map.MapStateStore
import maplibreGL.FormationMap
import maplibreGL.LngLat
import maplibreGL.MaplibreGeoJSON
import maplibreGL.MaplibreMarkerOptions
import maplibreGL.Marker
import maplibreGL.generateMapMarker
import maplibreGL.renderer.ADDITIONAL_GEOMETRY
import maplibreGL.renderer.GeoJsonGeometryRender
import maplibreGL.toJs
import mapsearch.ClusterService
import mapsearch.MapRenderOverrides
import mapsearch.MarkerGenerators.makeSvgMarkerForMap
import mapsearch.buildings.ActiveFloorsStore
import mapsearch.generalMarkers.connectableshapes.DeleteConnectionConnectorIdStore
import mapsearch.generalMarkers.connectableshapes.NewConnectableShapeConnectionStore
import mapsearch.markerEventListener
import mapsearch.tiles
import mu.KotlinLogging
import search.getSvgIconOptions
import svgmarker.MarkerSize

private val log = KotlinLogging.logger("GeneralMarkersSearchSpecStore")

data class GeneralMarkersSearchSpec(
    val includedGeneralMarkers: Set<String>? = null,
    val siteRestriction: SiteRestriction? = null
)

class GeneralMarkersSearchSpecStore : RootStore<GeneralMarkersSearchSpec>(GeneralMarkersSearchSpec(), Job()) {

    fun initialize() {
        startGeneralMarkerSearch()
        processOverrideChange()
        processFloorChanges()
    }

    val includeGeneralMarkers = handle<Set<String>?> { current, includedIds ->
        current.copy(includedGeneralMarkers = includedIds)
    }

    fun removeGeneralMarkersFromMap(generalMarkerIds: List<String>) {
        // remove markers from map
        generalMarkerIds.forEach { id ->
            currentRenderedGeneralMarkers[id]?.remove()
        }

    }

    private fun processOverrideChange() {
        withKoin {
            val mapRenderOverrides: MapRenderOverrides = get()
            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()

            mapRenderOverrides.pathToolActive.data handledBy { _ ->
                currentRenderedGeneralMarkers.keys.mapNotNull { generalMarkerId ->
                    generalMarkerLookUpStore.current[generalMarkerId]
                }.let { generalMarkers ->
                    reRenderMultipleGeneralMarkers(generalMarkers)
                }
            }
        }
    }

    private fun processFloorChanges() {
        withKoin {
            val activeFloorsStore = get<ActiveFloorsStore>()

            activeFloorsStore.data handledBy { floorIds ->
                try {
//                    log.info { "New SiteRestriction ${floorIds.toString()}" }
                    current.let { oldSpec ->
                        val newSiteRestriction = if (!floorIds.isNullOrEmpty()) {
                            oldSpec.siteRestriction?.copy(floorIds = floorIds)
                                ?: SiteRestriction(floorIds = floorIds)
                        } else null
                        update(oldSpec.copy(siteRestriction = newSiteRestriction))
                    }
                } catch (e: Exception) {
                    log.error(e) { "error processing site restriction for search spec" }
                }
            }
        }
    }

    fun addOrUpdateExistingGeneralMarker(generalMarker: GeoObjectDetails) {
        console.log("updateExistingGeneralMarker", generalMarker.title, generalMarker.latLon.toString())
        withKoin {
            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()
            generalMarkerLookUpStore.upsertGeneralMarker(generalMarker)
        }.also {
            reRenderSingleGeneralMarkerAndShapes(generalMarker)
        }
    }

    private fun startGeneralMarkerSearch() {
        withKoin {
            val mapStateStore: MapStateStore = get()
            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()
            val generalMarkersTilesLookUp: GeneralMarkersTilesLookUpStore = get()
            val clusterService: ClusterService = get()

            combine(
                generalMarkersTilesLookUp.data,
                data,
                mapStateStore.data,
            ) { tiles, spec, map -> Triple(tiles, spec, map) } handledBy { (tilesLookup, generalMarkerSpec, mapState) ->
                try {
                    if (mapState != null) {
                        delay(300) // let clustering a bit time before it gets triggered again
                        val ids = mapState.tiles().let { (zoom, tiles) ->
                            tiles.flatMap { tile ->
                                tilesLookup[tile].orEmpty()
                            }
                        }
                        // update currentBuildingsStore
                        val uniqueGeneralMarkers = ids.mapNotNull { id ->
                            // Check "GeneralMarkerSpec.includedGeneralMarkers":
                            // not null => GlobalSearch is active -> only show generalMarkers included search results, if empty show none
                            // null => MapSearch -> show all generalMarkers
                            if (generalMarkerSpec.includedGeneralMarkers == null || id in generalMarkerSpec.includedGeneralMarkers) {
                                generalMarkerLookUpStore.current[id]?.let { generalMarker ->
                                    // Apply Site Restrictions
                                    val activeFloorIds = generalMarkerSpec.siteRestriction?.floorIds
                                    val showMarker = if (!activeFloorIds.isNullOrEmpty()) {
                                        (generalMarker.tags.floorId?.let { floorId ->
                                            floorId in activeFloorIds
                                        } ?: true)
                                    } else true

                                    if (showMarker) {
                                        id to generalMarker
                                    } else {
                                        console.log("DON'T SHOW GeneralMarker: ${generalMarker.title}")
                                        null
                                    }
                                }
                            } else null
                        }.toMap()
                        // send to ClusterService
                        clusterService.setFeaturesFromGeneralMarkerSearch(uniqueGeneralMarkers)
//                        renderGeneralMarkers(uniqueGeneralMarkers)
                    }
                } catch (e: Exception) {
                    log.error(e) { "Error handling new searchSpec" }
                }
            }
        }
    }

    private val currentRenderedGeneralMarkers = mutableMapOf<String, Marker>()
    private val currentRenderedConnectableShapes = mutableMapOf<String, List<MaplibreGeoJSON>>()

    fun reRenderSingleGeneralMarkerAndShapes(generalMarker: GeoObjectDetails) {
//        log.info { "Re-Render single generalMarker: ${generalMarker.title}" }
        withKoin {
            val formationMap: FormationMap = get()
            val mapRenderOverrides: MapRenderOverrides = get()
//            val generalMarkersTilesLookUpStore: GeneralMarkersTilesLookUpStore = get()
//            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()
            val map = formationMap.map
            // Re-render Marker
            currentRenderedGeneralMarkers.remove(generalMarker.id)?.remove()
            createGeneralMarker(generalMarker)?.let { newMarker ->
                currentRenderedGeneralMarkers[generalMarker.id] = newMarker
                if (generalMarker.id !in mapRenderOverrides.hiddenMarkers) {
                    newMarker.addTo(map)
                }
            }
//            generalMarkerLookUpStore.updateGeneralMarker(generalMarker)
//            generalMarkersTilesLookUpStore.updateGeneralMarkerInTileLookUp(generalMarker.id, generalMarker.latLon)
            // Re-render geometry
            removeConnectedShapeGeoJSONGeometryFromMap(generalMarker.id)
            createConnectableShapeGeoJSONGeometries(generalMarker)?.let { geoJSONs ->
                currentRenderedConnectableShapes[generalMarker.id] = geoJSONs
                formationMap.geoJSONAdd(
                    features = geoJSONs.map { it.feature },
                    sourceId = ADDITIONAL_GEOMETRY,
                    replaceExisting = true,
                )
            }
        }
    }

    private fun reRenderMultipleGeneralMarkers(generalMarkers: List<GeoObjectDetails>) {
        withKoin {
            val formationMap: FormationMap = get()
            val map = formationMap.map

            generalMarkers.forEach { generalMarker ->
//                console.log("Re-render Building", generalMarker.title)
                currentRenderedGeneralMarkers.remove(generalMarker.id)?.let { marker ->
                    marker.remove()
                    createGeneralMarker(generalMarker)?.let { newMarker ->
                        currentRenderedGeneralMarkers[generalMarker.id] = newMarker
                        newMarker.addTo(map)
                    }
                }
            }
        }
    }

    private fun createGeneralMarker(generalMarker: GeoObjectDetails): Marker? {
        withKoin {
            val activeObjectStore: ActiveObjectStore = get()
            val localSettingsStore: LocalSettingsStore = get()
            val mapRenderOverrides: MapRenderOverrides = get()

            return getSvgIconOptions(
                obj = generalMarker,
                flagged = generalMarker.isFlagged,
                desaturated = mapRenderOverrides.pathToolActive.current,
                showStateIndicator = true,
            )?.let { svgIconOptions ->
                val markerOptions = MaplibreMarkerOptions(
                    element = makeSvgMarkerForMap(
                        id = generalMarker.id,
                        title = generalMarker.title,
                        svgIconOptions = svgIconOptions.copy(size = MarkerSize.S),
                        objectType = ObjectType.GeneralMarker,
                        active = activeObjectStore.current.id == generalMarker.id,
                        showTitle = !localSettingsStore.current.hideMarkerTitles,
                    ).also { element ->
                        if (!svgIconOptions.desaturated) { // make non-clickable when desaturated (= disabled)
                            element.addEventListener("click", markerEventListener)
                        }
                    },
                )
                val newMarker = Marker(markerOptions.toJs())
                newMarker.setLngLat(LngLat(generalMarker.latLon.lon, generalMarker.latLon.lat))
                newMarker
            }
        }
    }

    private fun createConnectableShapeGeoJSONGeometries(generalMarker: GeoObjectDetails): List<MaplibreGeoJSON>? {
        withKoin {
            val activeObjectStore: ActiveObjectStore = get()
            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()
            val newConnectableShapeConnectionStore: NewConnectableShapeConnectionStore = get()
            val deleteConnectionConnectorIdStore: DeleteConnectionConnectorIdStore = get()
            val geoJsonGeometryRender: GeoJsonGeometryRender = get()

            return generateMapMarker(
                geoObject = generalMarker,
                connectables = generalMarkerLookUpStore.getConnectableGeoShapesMap(generalMarker),
//                connectables = generalMarkerLookUpStore.connectableShapesCacheMap,
                overlappingShapeIds = generalMarkerLookUpStore.getOverlappingGeoShapeIds(generalMarker),
                activeObjectId = activeObjectStore.current.id,
                activeSourceConnectorId = newConnectableShapeConnectionStore.current.sourceConnectorId,
                activeTargetConnectorId = newConnectableShapeConnectionStore.current.targetConnectorId,
                activeTargetId = newConnectableShapeConnectionStore.current.targetMarkerId,
                activeConnectionDeleteConnectorId = deleteConnectionConnectorIdStore.current,
                highlightedConnector = geoJsonGeometryRender.mouseOverConnector,
                highlightedConnection = geoJsonGeometryRender.mouseOverConnection,
            )?.geoJSONs
        }
    }

    fun updateGeneralMarkerGeometryById(id: String) {
        withKoin {
            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()
            generalMarkerLookUpStore.current[id]?.let { generalMarker ->
                updateGeneralMarkerGeometry(generalMarker)
            }
        }
    }

    fun updateGeneralMarkerGeometry(
        geoObject: GeoObjectDetails? = null,
        newGeometry: Geometry? = null,
    ) {

        withKoin {
            val formationMap: FormationMap = get()
            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()
            val activeObjectStore: ActiveObjectStore = get()

            (geoObject ?: activeObjectStore.current.takeIf {
                it.objectType == ObjectType.GeneralMarker
            })?.let { generalMarker ->
                generalMarkerLookUpStore.upsertGeneralMarker(
                    generalMarker.copy(geometry = newGeometry),
                )

                formationMap.geoJsonUpdateData(
                    formationMap.createGeoJSONSourceDiff(
                        update = createConnectableShapeGeoJSONGeometries(generalMarker)?.let { geoJSONs ->
                            currentRenderedConnectableShapes[generalMarker.id] = geoJSONs
                            geoJSONs.map { geoJSON ->
                                formationMap.createGeoJSONFeatureDiff(
                                    id = geoJSON.id,
                                    newGeometry = geoJSON.geometry,
                                    removeAllProperties = true,
                                    addOrUpdateProperties = (geoJSON.properties.let {
                                        js("Object.keys(it)")
                                    } as Array<String>).map { key ->
                                        formationMap.createGeoJSONFeatureDiffProperty(key, geoJSON.properties[key])
                                    }.toTypedArray(),
                                )
                            }
                        }.orEmpty(),
                    ),
                    ADDITIONAL_GEOMETRY,
                )
            }
        }
    }

    /**
     * Removes the connected shape geometry from the map
     */
    private fun removeConnectedShapeGeoJSONGeometryFromMap(generalMarkerId: String) {
        withKoin {
            val formationMap: FormationMap = get()
            currentRenderedConnectableShapes.remove(generalMarkerId)?.let { shapeGeometries ->
                formationMap.geoJSONRemove(
                    shapeGeometries.map { it.id }.toTypedArray(),
                    ADDITIONAL_GEOMETRY,
                )
            }
        }
    }

    /**
     * Removes a generalMarker completely from the map, marker and geometry
     */
    fun removeGeneralMarkerFromMap(generalMarkerId: String) {
        currentRenderedGeneralMarkers.remove(generalMarkerId)?.remove()
        removeConnectedShapeGeoJSONGeometryFromMap(generalMarkerId)
    }

    fun removeGeneralMarkersFromMap(generalMarkerIds: Set<String>) {
        generalMarkerIds.forEach { generalMarkerId ->
            removeGeneralMarkerFromMap(generalMarkerId)
        }
    }

    suspend fun renderGeneralMarkers(generalMarkers: Map<String, GeoObjectDetails>, batchSize: Int = 10) {
        withKoin {
            val formationMap: FormationMap = get()
            val mapRenderOverrides: MapRenderOverrides = get()
            val map = formationMap.map

            // Remove markers that no longer exist in generalMarkers
            (currentRenderedGeneralMarkers.keys - generalMarkers.keys).forEach { id ->
                removeGeneralMarkerFromMap(id)
            }

            val newGeneralMarkers = generalMarkers.filter { (id, _) -> id !in currentRenderedGeneralMarkers.keys }
            val markerBatches = newGeneralMarkers.entries.chunked(batchSize)

            coroutineScope {
                for (batch in markerBatches) {
                    // Run calculations in parallel for better performance
                    val results = batch.map { (generalMarkerId, generalMarker) ->
                        async(Dispatchers.Default) {
                            val geoJSONs = createConnectableShapeGeoJSONGeometries(generalMarker) ?: emptyList()
                            val marker = createGeneralMarker(generalMarker)
                            Triple(generalMarkerId, marker, geoJSONs)
                        }
                    }.awaitAll()

                    // Update markers & geometries efficiently
                    results.forEach { (generalMarkerId, marker, geoJSONs) ->
                        marker?.let {
                            currentRenderedGeneralMarkers[generalMarkerId] = it
                            if (generalMarkerId !in mapRenderOverrides.hiddenMarkers) {
                                it.addTo(map)
                            }
                        }
                        currentRenderedConnectableShapes[generalMarkerId] = geoJSONs
                    }

                    // Add all new geometries at once (reducing multiple map updates)
                    formationMap.geoJSONAdd(
                        results.flatMap { it.third }.map { it.feature },
                        ADDITIONAL_GEOMETRY,
                    )
                }
            }
        }
    }
}
